import React, { useEffect, useMemo, useState } from 'react';
import { FaBuilding, FaPlus, FaServer } from 'react-icons/fa';
import { useStateContext } from '../../contexts/ContextProvider';
import { apiCall } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';

const ClientSettings: React.FC = () => {
  const { isAdminUser } = useStateContext();
  const { token } = useAuth();

  const [clients, setClients] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [facilitiesText, setFacilitiesText] = useState('Main Facility\nSecondary Facility');
  const [showCreateWizard, setShowCreateWizard] = useState(false);

  const canEdit = useMemo(() => isAdminUser, [isAdminUser]);

  const loadClients = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiCall('/core/admin/clients/', { method: 'GET' }, token);
      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || `HTTP ${response.status}`);
      }
      const data = await response.json();
      setClients(data.results || []);
    } catch (e: any) {
      setError(`Failed to load clients: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, [token]);

  const handleCreateClient = async () => {
    if (!token) return;
    if (!name.trim()) {
      setError('Client name is required.');
      return;
    }

    setError('');
    setMessage('');
    setLoading(true);

    const facilities = facilitiesText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

    try {
      const response = await apiCall('/core/admin/clients/', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          contact_email: contactEmail.trim(),
          subdomain: subdomain.trim().toLowerCase(),
          facilities,
        }),
      }, token);

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || JSON.stringify(data));
      }

      setMessage(`Created ${data.client?.name}. URL: https://${data.client?.subdomain}.${data.client?.domain}/`);
      setName('');
      setContactEmail('');
      setSubdomain('');
      await loadClients();
    } catch (e: any) {
      setError(`Failed to create client: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isAdminUser) {
    return (
      <div className="p-6">
        <div className="alert alert-warning">
          <span>Only administrators can manage client-level settings.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
          <FaBuilding className="text-primary" />
          Clients
        </h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateWizard((prev) => !prev)}
          disabled={loading || !canEdit}
        >
          <FaPlus className="mr-2" /> {showCreateWizard ? 'Close Wizard' : 'Add Client'}
        </button>
      </div>

      {message && (
        <div className="alert alert-success">
          <span>{message}</span>
        </div>
      )}
      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
        </div>
      )}

      <div className="card bg-base-100 border border-base-300 shadow-sm">
        <div className="card-body">
          <h2 className="card-title flex items-center gap-2"><FaServer /> Clients</h2>
          <div className="overflow-x-auto">
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>URL</th>
                  <th>Database</th>
                  <th>Facilities</th>
                </tr>
              </thead>
              <tbody>
                {clients.length === 0 ? (
                  <tr><td colSpan={5} className="text-center">{loading ? 'Loading...' : 'No clients provisioned yet'}</td></tr>
                ) : clients.map((client) => (
                  <tr key={client.id}>
                    <td>{client.name}</td>
                    <td>{client.slug}</td>
                    <td>{`https://${client.subdomain}.${client.domain}/`}</td>
                    <td>{client.database_name}</td>
                    <td>{Array.isArray(client.facilities) ? client.facilities.join(', ') : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showCreateWizard && (
        <div className="card bg-base-100 border border-base-300 shadow-sm">
          <div className="card-body space-y-4">
            <h2 className="card-title">Add New Client Wizard</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="form-control">
                <span className="label"><span className="label-text">Client Name</span></span>
                <input className="input input-bordered" value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              <label className="form-control">
                <span className="label"><span className="label-text">Contact Email</span></span>
                <input className="input input-bordered" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
              </label>
              <label className="form-control">
                <span className="label"><span className="label-text">Subdomain</span></span>
                <input
                  className="input input-bordered"
                  placeholder="e.g. demo"
                  value={subdomain}
                  onChange={(e) => setSubdomain(e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase())}
                />
                <span className="label"><span className="label-text-alt">Will create: https://{subdomain || 'clientname'}.templaredge.com/</span></span>
              </label>
              <label className="form-control md:col-span-2">
                <span className="label"><span className="label-text">Initial Facilities (one per line)</span></span>
                <textarea className="textarea textarea-bordered" rows={4} value={facilitiesText} onChange={(e) => setFacilitiesText(e.target.value)} />
              </label>
            </div>
            <div className="card-actions justify-end">
              <button className="btn btn-primary" onClick={handleCreateClient} disabled={loading || !canEdit}>
                <FaPlus className="mr-2" /> Create Client
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSettings;
